import { useMemo } from 'react';

import { AnyAbility } from '@casl/ability';
import { useAbility } from '@casl/react';

import { Permission } from '@/api/client';

import { useAuth } from '../../_hooks/use-auth';
import { usePermissionOverrides } from '../../_hooks/use-permission-overrides';
import {
  createEffectiveAbilityFromRoles,
  getEffectiveCaslOverrideMap,
  rolesForProperty,
  type UserRoles,
} from '../../ability-from-session';

const abilityCan = (
  ability: AnyAbility,
  permissions: Permission | Permission[],
  condition: 'any' | 'all'
): boolean => {
  if (!Array.isArray(permissions)) {
    return ability.can(permissions.action, permissions.resource);
  }
  return condition === 'any'
    ? permissions.some((p) => ability.can(p.action, p.resource))
    : permissions.every((p) => ability.can(p.action, p.resource));
};

/** Only sessions whose roles carry permissions can be narrowed per property. */
const rolesCarryPermissions = (roles: UserRoles | null | undefined): boolean =>
  (roles ?? []).some((role) => (role.permissions?.length ?? 0) > 0);

/**
 * Permission check scoped to a single property.
 *
 * {@link usePermission} answers "can this user do X *somewhere* in the tenant" — it
 * flattens every role assignment into one ability. That is wrong for actions taken
 * against a specific property: a user who is a VP over two properties and a Leasing
 * Agent elsewhere would be offered VP-only actions on the Leasing Agent property, and
 * the API — which authorizes per property — then rejects the submit.
 *
 * This evaluates the same permissions against only the roles the backend would use for
 * `propertyId`: tenant-wide roles (no `property_id`) plus roles scoped to that property.
 *
 * Falls back to the session-wide ability when there is nothing to scope with — no
 * property in context, an admin (authorized everywhere server-side), or a session whose
 * roles carry no permissions (mocked contexts, where the ability is supplied directly).
 *
 * @param permissions - Permission (or list) to check
 * @param propertyId - Property the action targets
 * @param condition - 'any' (default) or 'all' when passing a list
 */
export const usePropertyPermission = (
  permissions: Permission | Permission[],
  propertyId: string | null | undefined,
  condition: 'any' | 'all' = 'any'
): boolean => {
  const sessionAbility = useAbility();
  const { user, roles } = useAuth();
  const { localOverrideEpoch } = usePermissionOverrides();

  const propertyAbility = useMemo(() => {
    if (!user || user.is_admin || !propertyId) return null;
    if (!rolesCarryPermissions(roles)) return null;
    return createEffectiveAbilityFromRoles(
      user,
      rolesForProperty(roles, propertyId),
      getEffectiveCaslOverrideMap(user)
    );
    // localOverrideEpoch re-reads the localStorage override maps, matching AuthProvider.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- localOverrideEpoch triggers recompute when staff edits overrides
  }, [user, roles, propertyId, localOverrideEpoch]);

  return abilityCan(propertyAbility ?? sessionAbility, permissions, condition);
};
